<template>
  <div class="pb-6" data-cy="cf-submission-list">
    <!-- Wraps because the title plus the filter plus the button exceed a phone's
         width; without it the action group overflows the list's right edge. -->
    <div
      class="mb-1 flex flex-wrap items-start justify-between gap-x-4 gap-y-2"
    >
      <h3 class="page-header-2 mb-0">Forms &amp; Checklists</h3>

      <!-- Filter and attach sit together on the header row: both act on the list
           as a whole, and the toggle stacked under the button read as though it
           belonged to the summary line instead. -->
      <div class="flex shrink-0 items-center gap-4">
        <!-- Only once something is archived: until then it filters nothing.
             All on the left, Active on the right, matching
             ProgressBillingSchedulesList — the platform's one prior art for an
             active/archived filter over a card list. -->
        <ToggleSwitch
          v-if="archivedCount"
          v-model="showActive"
          label-on="Active"
          label-off="All"
        />
        <SeraButton
          text="Attach form"
          icon="fa-solid fa-plus"
          data-cy="cf-attach-btn"
          @click="emit('attach')"
        />
      </div>
    </div>

    <!-- How many forms, and how many can hold the appointment open. -->
    <p
      v-if="submissions.length"
      class="mb-4 text-sm text-gray-500"
      data-cy="cf-summary-line"
    >
      {{ summary }}
    </p>

    <div
      v-if="!submissions.length"
      class="flex flex-col items-center px-10 py-12 text-center"
      data-cy="cf-empty-state"
    >
      <i class="fad fa-clipboard-list-check text-5xl text-gray-300"></i>
      <p class="mt-4 text-lg font-medium text-gray-900">No forms attached</p>
      <p class="mt-2 text-sm text-gray-500">
        Attach a published form to track it on this job.
      </p>
    </div>

    <!-- Everything on the job is archived, so Active is legitimately empty. -->
    <div
      v-else-if="!visibleSubmissions.length"
      class="flex flex-col items-center px-10 py-12 text-center"
      data-cy="cf-no-active-state"
    >
      <i class="fad fa-clipboard-list-check text-5xl text-gray-300"></i>
      <p class="mt-4 text-lg font-medium text-gray-900">No active forms</p>
      <p class="mt-2 text-sm text-gray-500">
        Turn the filter off to see the {{ archivedCount }} archived
        {{ archivedCount === 1 ? "form" : "forms" }} on this job.
      </p>
    </div>

    <ul v-else class="space-y-3">
      <li v-for="submission in visibleSubmissions" :key="submission.id">
        <!-- A container rather than one big button: the row needs its own action
             alongside the select target, and nesting a button inside a button is
             invalid markup that swallows the inner click. -->
        <div
          :class="['cf-card', isArchived(submission) && 'cf-card--archived']"
        >
          <button
            type="button"
            class="cf-card-main"
            :data-cy="`cf-submission-row-${submission.id}`"
            @click="emit('select', submission)"
          >
            <div class="flex min-w-0 flex-1 items-start gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="min-w-0 truncate font-medium text-gray-900">
                    {{ submission.name }}
                  </p>
                  <CustomFormBadge
                    v-if="isArchived(submission)"
                    class="shrink-0"
                    :data-cy="`cf-archived-badge-${submission.id}`"
                  >
                    Archived
                  </CustomFormBadge>
                  <!-- An archived form no longer gates close-out, so the badge
                       that says it does would be a lie. -->
                  <CustomFormBadge
                    v-else-if="submission.required_for_close"
                    tone="warn"
                    class="shrink-0"
                  >
                    Required to close
                  </CustomFormBadge>
                </div>
                <p class="mt-1 text-xs text-gray-500" data-cy="cf-card-subtext">
                  {{ cardSubtext(submission) }}
                </p>
              </div>
              <CustomFormSubmissionStatusChip :status="submission.status" />
            </div>

            <CustomFormProgressBar
              class="w-full shrink-0 sm:w-44"
              :answered="submission.progress.required_answered"
              :total="submission.progress.required_total"
              :complete="submission.progress.complete"
              :data-cy="`cf-progress-bar-${submission.id}`"
              :label-data-cy="`cf-progress-${submission.id}`"
            />

            <i
              class="fas fa-chevron-right hidden shrink-0 text-gray-400 sm:block"
            ></i>
          </button>

          <!-- Icon-only behind a tooltip, matching MediaCard on this same page and
               ProgressBillingScheduleCard. Still not a kebab: Send is offered
               only on a submitted, unarchived form, so most rows carry one icon
               and none carries more than two. Revisit if Download PDF ships and
               a row can hold three. -->
          <div class="cf-card-action">
            <!-- Withheld rather than disabled on anything else: the server
                 refuses an unsubmitted or archived form outright, so the button
                 would be one that always fails. Same reasoning as Submit in the
                 detail view. -->
            <Tooltip v-if="isSendable(submission)" placement="top">
              <template #trigger>
                <SeraButton
                  icon="fa-regular fa-paper-plane"
                  :data-cy="`cf-send-${submission.id}`"
                  @click="emit('send', submission)"
                />
              </template>
              <template #tooltip>Send to customer</template>
            </Tooltip>
            <Tooltip placement="top">
              <template #trigger>
                <SeraButton
                  v-if="isArchived(submission)"
                  icon="fa-regular fa-box-open"
                  :data-cy="`cf-unarchive-${submission.id}`"
                  @click="emit('unarchive', submission)"
                />
                <SeraButton
                  v-else
                  icon="fa-regular fa-archive"
                  :data-cy="`cf-archive-${submission.id}`"
                  @click="emit('archive', submission)"
                />
              </template>
              <template #tooltip>
                {{ isArchived(submission) ? "Restore form" : "Archive form" }}
              </template>
            </Tooltip>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import SeraButton from "@/components/Button.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { CustomFormSubmission } from "@/typings/customForm";
import CustomFormBadge from "./CustomFormBadge.vue";
import CustomFormProgressBar from "./CustomFormProgressBar.vue";
import CustomFormSubmissionStatusChip from "./CustomFormSubmissionStatusChip.vue";

// Receives every submission on the job, archived included, and hides the archived
// ones itself. The tab cannot pre-filter: it feeds the same payload to the attach
// picker's exclude list, which has to know about archived templates or it offers
// one that then 409s on the unique index.
const props = defineProps<{ submissions: CustomFormSubmission[] }>();

const emit = defineEmits<{
  attach: [];
  select: [submission: CustomFormSubmission];
  send: [submission: CustomFormSubmission];
  archive: [submission: CustomFormSubmission];
  unarchive: [submission: CustomFormSubmission];
}>();

const showActive = ref(true);

function isArchived(submission: CustomFormSubmission): boolean {
  return Boolean(submission.archived_at);
}

// The two conditions the send endpoint refuses on that a row can see. The third,
// a form with nothing customer-visible in it, is not on the list payload, so that
// one is left to the server and surfaced as the modal's error.
//
// Withheld rather than offered behind a confirmation: the server refuses an
// unsubmitted form outright, so the prompt would confirm into a guaranteed 422.
// That reads "incomplete" as "not submitted". A submitted form with unanswered
// OPTIONAL questions is also reachable and is sendable — nothing here stands in
// its way — and whether that one deserves a prompt is a product question rather
// than something this gate should decide.
function isSendable(submission: CustomFormSubmission): boolean {
  return submission.status === "submitted" && !isArchived(submission);
}

const activeSubmissions = computed(() =>
  props.submissions.filter((submission) => !isArchived(submission))
);

const archivedCount = computed(
  () => props.submissions.length - activeSubmissions.value.length
);

const visibleSubmissions = computed(() =>
  showActive.value ? activeSubmissions.value : props.submissions
);

// Counts the active forms whichever way the filter is set: "attached" and
// "required to close" are both statements about what still applies to this job,
// and an archived form does neither.
const summary = computed(() => {
  const count = activeSubmissions.value.length;
  const gating = activeSubmissions.value.filter(
    (submission) => submission.required_for_close
  ).length;
  const parts = [`${count} ${count === 1 ? "form" : "forms"} attached`];
  if (gating > 0) parts.push(`${gating} required to close`);
  if (!showActive.value) parts.push(`${archivedCount.value} archived`);
  return parts.join(" · ");
});

// Kept because the tech app shows it.
function attachedVia(submission: CustomFormSubmission): string {
  return submission.attached_via === "manual"
    ? "Manually attached"
    : "Auto-attached";
}

// A job can hold several copies of one template — one per visit, and more than one
// on a visit two technicians shared — which otherwise render as identical cards.
// The anchor is what tells them apart, so it rides the subtext line that already
// carries this card's provenance.
//
// A copy with no anchor belongs to the whole job rather than to any one visit, so it
// gets nothing here: a placeholder would imply a visit it does not have.
function cardSubtext(submission: CustomFormSubmission): string {
  const parts = [attachedVia(submission)];
  if (typeof submission.appointment_id === "number") {
    parts.push(`Appointment ${submission.appointment_id}`);
  }
  return parts.join(" · ");
}
</script>

<style scoped>
.cf-card {
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  border: 1px solid rgb(229 231 235);
  border-radius: 0.75rem;
  background: #fff;
  padding: 1rem 1.25rem;
  text-align: left;
  transition: border-color 150ms ease;
}
.cf-card-main {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  border: 0;
  background: transparent;
  /* Right gutter reserves the corner the absolutely-positioned action occupies on
     mobile; without it the icon lands on top of the status chip. */
  padding: 0 2.25rem 0 0;
  text-align: left;
}
/* Stacked on a phone, one aligned row from the small breakpoint up. */
@media (min-width: 640px) {
  .cf-card {
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
  }
  .cf-card-main {
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
    padding-right: 0;
    /* flex + min-width:0 rather than width:100%: as a flex child its default
       min-width:auto refuses to shrink below its content, so the row's badges and
       progress bar push the whole page into horizontal overflow once the action
       button sits beside it. */
    flex: 1;
    min-width: 0;
  }
}
.cf-card:hover {
  border-color: rgb(156 163 175);
}
.cf-card-main:focus-visible,
.cf-card-action:focus-visible {
  outline: 2px solid rgb(28 100 242);
  outline-offset: 2px;
}
/* Muted, not hidden: an archived form is still a record of the visit. */
.cf-card--archived {
  background: rgb(249 250 251);
}
.cf-card--archived .cf-card-main {
  opacity: 0.65;
}
/* Stacked layout puts the action last, bottom-left, which reads as orphaned and
   sits furthest from the thumb. Pin it to the card's top-right instead; from the
   small breakpoint up it rejoins the flex row. */
.cf-card-action {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.375rem;
}
@media (min-width: 640px) {
  .cf-card-action {
    position: static;
    align-self: center;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cf-card {
    transition: none;
  }
}
</style>
